/**
 * SectionUnits — tipos de alojamiento, unidades físicas y reparto automático.
 *
 * Es la sección donde el producto tiene que explicar bien dos conceptos que la
 * arquitectura mantiene separados (PRODUCT_V2_SPEC §2.1):
 *
 *   tipo de alojamiento → lo que se vende (tarifa, capacidad, contenido)
 *   grupo de reparto    → OPCIONAL, solo lo usa el reparto automático
 *
 * En Dolce coinciden uno a uno, pero la interfaz no da por hecho que sea así:
 * el grupo se muestra como una capa aparte que puede apagarse entera.
 *
 * La matemática del reparto (pesos 70/30, horizonte, desempates) vive detrás
 * de "Configuración avanzada": nadie debería necesitarla para configurar una
 * propiedad.
 */
import React, { useState } from "react";
import { Users, Bed, Image as ImageIcon, Sparkle, Globe, PencilSimple, Scales } from "@phosphor-icons/react";
import Chip from "@/components/ui-dv/Chip";
import SidePanel from "@/components/ui-dv/SidePanel";
import {
  Block, Card, FieldGrid, Field, TextArea, Toggle, Note, Advanced, ApprovedMark,
} from "@/pages/admin/pms/settings/SettingsPrimitives";
import {
  useSettingsV2, updateUnitType, updateUnit, updateAllocation, unitLabelFor,
} from "@/lib/settingsV2Store";
import { UNIT_STATUS_LABEL } from "@/data/settingsV2Demo";

export default function SectionUnits() {
  const { property, unitTypes, units, pools, allocation } = useSettingsV2();
  const label = unitLabelFor(property);
  const [editingType, setEditingType] = useState(null);
  const [editingUnit, setEditingUnit] = useState(null);

  const typeById = Object.fromEntries(unitTypes.map((t) => [t.unit_type_id, t]));
  const editingTypeData = unitTypes.find((t) => t.unit_type_id === editingType) || null;
  const editingUnitData = units.find((u) => u.unit_id === editingUnit) || null;

  return (
    <div data-testid="section-units">
      {/* -- Tipos ------------------------------------------------------- */}
      <Block
        title={`Tipos de ${label.singular.toLowerCase()}`}
        description="La categoría que el huésped reserva. Define tarifa, capacidad y el contenido que verá en la web."
        testId="units-types"
      >
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {unitTypes.map((t) => (
            <UnitTypeCard
              key={t.unit_type_id}
              type={t}
              unitLabel={label}
              onEdit={() => setEditingType(t.unit_type_id)}
            />
          ))}
        </div>
      </Block>

      {/* -- Unidades ---------------------------------------------------- */}
      <Block
        title={label.plural}
        description={`Cada ${label.singular.toLowerCase()} física: lo que se reserva, se limpia y tiene propietario.`}
        testId="units-list"
      >
        <Card padded={false}>
          <ul className="divide-y divide-stone-100 dark:divide-stone-800" data-testid="units-grid">
            {units.map((u) => (
              <li key={u.unit_id}>
                <button
                  type="button"
                  onClick={() => setEditingUnit(u.unit_id)}
                  data-testid={`unit-row-${u.unit_id}`}
                  className="w-full text-left flex items-center gap-3 px-4 min-h-[56px] py-2.5 hover:bg-stone-50 dark:hover:bg-stone-900 transition-colors"
                >
                  <span className="w-11 shrink-0 text-sm text-stone-900 dark:text-stone-100 tabular-nums">
                    {u.unit_id}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-stone-900 dark:text-stone-100 truncate">
                      {u.name}
                    </span>
                    <span className="block text-xs text-stone-500 dark:text-stone-400 truncate">
                      {typeById[u.unit_type_id]?.name}
                    </span>
                  </span>
                  {!u.owner_assigned && (
                    <Chip tone="stone" size="xs">Sin propietario</Chip>
                  )}
                  <Chip tone={u.status === "active" ? "emerald" : "amber"} size="xs" dot>
                    {UNIT_STATUS_LABEL[u.status]}
                  </Chip>
                  <PencilSimple size={14} className="shrink-0 text-stone-400" />
                </button>
              </li>
            ))}
          </ul>
        </Card>
      </Block>

      {/* -- Reparto ------------------------------------------------------ */}
      <Block
        title="Distribución automática"
        description="Cuando entra una reserva, el sistema elige qué unidad concreta la recibe para que el trabajo y los ingresos se repartan de forma equilibrada."
        testId="units-allocation"
      >
        <Card>
          <Toggle
            label={`Distribuir reservas automáticamente entre ${label.plural.toLowerCase()} equivalentes`}
            description="Si lo desactivas, cada reserva se asigna a mano."
            checked={allocation.enabled}
            onChange={(v) => updateAllocation({ enabled: v })}
            approved
            testId="allocation-enabled"
          />

          {allocation.enabled && (
            <div className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-800">
              <Note testId="allocation-pool-note">
                Los grupos de reparto son una capa aparte del tipo de alojamiento: definen qué
                unidades compiten entre sí. Aquí coinciden uno a uno, pero no tienen por qué.
              </Note>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="allocation-pools">
                {pools.map((p) => {
                  const type = typeById[p.unit_type_id];
                  return (
                    <div
                      key={p.pool_id}
                      className="rounded-lg border border-stone-200 dark:border-stone-800 p-4"
                      data-testid={`allocation-pool-${p.pool_id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Scales size={14} className="text-stone-400" />
                        <span className="text-sm text-stone-900 dark:text-stone-100">{p.name}</span>
                      </div>
                      <div className="text-xs text-stone-500 dark:text-stone-400">
                        {p.unit_ids.length} {label.plural.toLowerCase()} elegibles
                      </div>
                      <div className="text-[11px] text-stone-400 dark:text-stone-500 mt-1.5">
                        Tipo que reparte: {type ? type.name : "—"}
                      </div>
                    </div>
                  );
                })}
              </div>

              <Advanced testId="allocation-advanced">
                <Note>
                  Estos son los pesos aprobados del reparto. No hace falta entenderlos para
                  configurar la propiedad; se muestran para poder revisarlos.
                </Note>
                <FieldGrid>
                  <Field
                    label="Peso · ingresos"
                    type="number"
                    value={allocation.weight_revenue}
                    onChange={(v) => updateAllocation({ weight_revenue: v, weight_occupancy: 100 - v })}
                    suffix="%"
                    approved
                    testId="allocation-weight-revenue"
                  />
                  <Field
                    label="Peso · ocupación"
                    type="number"
                    value={allocation.weight_occupancy}
                    onChange={(v) => updateAllocation({ weight_occupancy: v, weight_revenue: 100 - v })}
                    suffix="%"
                    approved
                    testId="allocation-weight-occupancy"
                  />
                  <Field
                    label="Horizonte · mes en curso"
                    type="number"
                    value={allocation.horizon_current_month}
                    onChange={(v) => updateAllocation({ horizon_current_month: v, horizon_ytd: 100 - v })}
                    suffix="%"
                    approved
                    testId="allocation-horizon-month"
                  />
                  <Field
                    label="Horizonte · año en curso"
                    type="number"
                    value={allocation.horizon_ytd}
                    onChange={(v) => updateAllocation({ horizon_ytd: v, horizon_current_month: 100 - v })}
                    suffix="%"
                    approved
                    testId="allocation-horizon-ytd"
                  />
                </FieldGrid>
                <div className="mt-5">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-[11px] tracking-[0.18em] uppercase text-stone-500 dark:text-stone-400">
                      Desempates, en orden
                    </span>
                    <ApprovedMark />
                  </div>
                  <ol className="text-sm text-stone-600 dark:text-stone-300 space-y-1">
                    {allocation.tie_breakers.map((t, i) => (
                      <li key={t} className="flex gap-2">
                        <span className="text-stone-400 tabular-nums">{i + 1}.</span>
                        {t}
                      </li>
                    ))}
                  </ol>
                </div>
              </Advanced>
            </div>
          )}
        </Card>
      </Block>

      {/* -- Editores ------------------------------------------------------ */}
      <SidePanel
        open={!!editingTypeData}
        onClose={() => setEditingType(null)}
        title={editingTypeData?.name}
        subtitle={`Tipo de ${label.singular.toLowerCase()}`}
        testId="unit-type-panel"
      >
        {editingTypeData && (
          <div className="space-y-5">
            <FieldGrid cols={2}>
              <Field
                label="Nombre"
                value={editingTypeData.name}
                onChange={(v) => updateUnitType(editingTypeData.unit_type_id, { name: v })}
                usedIn="unit_types"
                testId="unit-type-name"
              />
              <Field
                label="Tarifa base"
                type="number"
                value={editingTypeData.base_rate_eur}
                onChange={(v) => updateUnitType(editingTypeData.unit_type_id, { base_rate_eur: v })}
                suffix="€"
                hint="Por noche"
                testId="unit-type-rate"
              />
              <Field
                label="Dormitorios"
                type="number"
                value={editingTypeData.bedrooms}
                onChange={(v) => updateUnitType(editingTypeData.unit_type_id, { bedrooms: v })}
                testId="unit-type-bedrooms"
              />
              <Field
                label="Capacidad"
                type="number"
                value={editingTypeData.sleeps}
                onChange={(v) => updateUnitType(editingTypeData.unit_type_id, { sleeps: v })}
                suffix="pers."
                testId="unit-type-sleeps"
              />
              <TextArea
                label="Descripción"
                value={editingTypeData.description}
                onChange={(v) => updateUnitType(editingTypeData.unit_type_id, { description: v })}
                testId="unit-type-description"
              />
            </FieldGrid>
            <div className="pt-4 border-t border-stone-100 dark:border-stone-800">
              <Note testId="unit-type-media-note">
                Las fotos y el equipamiento se cuentan aquí porque la web y el portal los
                usarán, pero gestionarlos todavía no forma parte de esta vista previa.
              </Note>
              <Toggle
                label="Visible en la web"
                description="Aparecerá como categoría reservable cuando publiques la web."
                checked={editingTypeData.website_visible}
                onChange={(v) => updateUnitType(editingTypeData.unit_type_id, { website_visible: v })}
                testId="unit-type-website-visible"
              />
            </div>
          </div>
        )}
      </SidePanel>

      <SidePanel
        open={!!editingUnitData}
        onClose={() => setEditingUnit(null)}
        title={editingUnitData ? `${editingUnitData.unit_id} · ${editingUnitData.name}` : ""}
        subtitle={label.singular}
        testId="unit-panel"
      >
        {editingUnitData && (
          <div className="space-y-5">
            <FieldGrid cols={2}>
              <Field
                label="Nombre"
                value={editingUnitData.name}
                onChange={(v) => updateUnit(editingUnitData.unit_id, { name: v })}
                usedIn="units"
                testId="unit-name"
              />
              <div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-stone-500 mb-1.5">
                  Tipo
                </div>
                <div className="text-sm text-stone-900 dark:text-stone-100 min-h-[44px] flex items-center">
                  {typeById[editingUnitData.unit_type_id]?.name}
                </div>
              </div>
            </FieldGrid>
            <div className="pt-4 border-t border-stone-100 dark:border-stone-800 space-y-1">
              <Toggle
                label="Operativa"
                description="Fuera de servicio deja de admitir reservas nuevas."
                checked={editingUnitData.status === "active"}
                onChange={(v) => updateUnit(editingUnitData.unit_id, { status: v ? "active" : "maintenance" })}
                testId="unit-status"
              />
              <Toggle
                label="Propietario asignado"
                description="Necesario para generar extractos y permitir estancias del propietario."
                checked={editingUnitData.owner_assigned}
                onChange={(v) => updateUnit(editingUnitData.unit_id, { owner_assigned: v })}
                testId="unit-owner-assigned"
              />
            </div>
          </div>
        )}
      </SidePanel>
    </div>
  );
}

function UnitTypeCard({ type, unitLabel, onEdit }) {
  return (
    <div className="dv-card flex flex-col" data-testid={`unit-type-card-${type.unit_type_id}`}>
      <div className="flex items-start gap-3">
        <span
          className="mt-1 w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: type.accent }}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm text-stone-900 dark:text-stone-100 truncate">
            {type.name}
          </div>
          <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5 line-clamp-2">
            {type.description}
          </p>
        </div>
        <button
          type="button"
          onClick={onEdit}
          data-testid={`unit-type-edit-${type.unit_type_id}`}
          className="dv-btn dv-btn-ghost text-xs shrink-0 min-h-[44px]"
        >
          Editar
        </button>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <Metric icon={Bed} label={unitLabel.plural} value={type.unit_count} />
        <Metric icon={Users} label="Capacidad" value={`${type.sleeps}`} />
        <Metric icon={Sparkle} label="Equipamiento" value={type.amenities_count} />
        <Metric icon={ImageIcon} label="Fotos" value={type.photos_count} />
      </div>

      <div className="mt-4 pt-3 border-t border-stone-100 dark:border-stone-800 flex items-center justify-between gap-3">
        <div className="text-sm text-stone-900 dark:text-stone-100 tabular-nums">
          {type.base_rate_eur} €
          <span className="text-xs text-stone-400"> / noche · tarifa base</span>
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] text-stone-500 dark:text-stone-400">
          <Globe size={12} />
          {type.website_visible ? "En la web" : "Oculto en la web"}
        </span>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-stone-400 mb-0.5">
        <Icon size={12} />
        <span className="text-[10px] uppercase tracking-wider truncate">{label}</span>
      </div>
      <div className="text-sm text-stone-900 dark:text-stone-100 tabular-nums">{value}</div>
    </div>
  );
}
